import { useAuth } from "@assetwatch/auth";
import { Alert, Container, Stack, Title } from "@assetwatch/ui";
import type { ReactNode } from "react";
import {
  MlFrontendFeatureDisabledError,
  MlFrontendUnauthorizedError,
} from "../errors/errors";
import { assertCanAccessMlFrontend } from "../lib/access";
import { useLaunchDarklyStatus } from "./LaunchDarkly/LaunchDarklyProvider";
import { useFeatureFlags } from "./LaunchDarkly/useFeatureFlags";

export function AppFeatureGate({ children }: { children: ReactNode }) {
  const flags = useFeatureFlags();
  const hasLaunchDarklyClient = Boolean(
    import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID,
  );
  const { isReady } = useLaunchDarklyStatus();
  const { user } = useAuth();

  if (hasLaunchDarklyClient && !isReady) {
    return (
      <Container size="sm">
        <Stack gap={16}>
          <Title size="lg">Loading ML Frontend</Title>
          <Alert
            tone="informative"
            title="Checking feature access..."
            subtext="Please wait while we confirm access."
          />
        </Stack>
      </Container>
    );
  }

  try {
    assertCanAccessMlFrontend({ flags, user });
  } catch (error) {
    if (error instanceof MlFrontendFeatureDisabledError) {
      return (
        <Container size="sm">
          <Stack gap={16}>
            <Alert
              tone="informative"
              title="App currently unavailable"
              subtext="This app is not enabled for your account."
            />
          </Stack>
        </Container>
      );
    }

    if (error instanceof MlFrontendUnauthorizedError) {
      return (
        <Container size="sm">
          <Stack gap={16}>
            <Alert
              tone="negative"
              title="Access denied"
              subtext="Contact the ML team if you feel you've reached this message in error."
            />
          </Stack>
        </Container>
      );
    }

    throw error;
  }

  return <>{children}</>;
}
